import { isAxiosError } from 'axios';

import type { ApiErrorPayload, ApiFieldError } from '@/api/error.api';
import { translateFile } from '@/i18n';

export class AppApiError extends Error {
  status?: number;
  code?: string;
  userMessage: string;
  errors?: Record<string, string[]>;
  fieldErrors?: ApiFieldError[];
  raw: unknown;

  constructor(params: {
    message: string;
    userMessage: string;
    status?: number;
    code?: string;
    errors?: Record<string, string[]>;
    fieldErrors?: ApiFieldError[];
    raw: unknown;
  }) {
    super(params.message);

    this.name = 'AppApiError';
    this.status = params.status;
    this.code = params.code;
    this.userMessage = params.userMessage;
    this.errors = params.errors;
    this.fieldErrors = params.fieldErrors;
    this.raw = params.raw;
  }
}

const getFallbackMessageByStatus = (status?: number) => {
  if (status === 400) return translateFile('utils.error', 'badRequest');
  if (status === 401) return translateFile('utils.error', 'unauthorized');
  if (status === 403) return translateFile('utils.error', 'forbidden');
  if (status === 404) return translateFile('utils.error', 'notFound');
  if (status && status >= 500) return translateFile('utils.error', 'serverError');

  return translateFile('utils.error', 'generic');
};

export const normalizeApiError = (error: unknown, defaultMessage?: string): AppApiError => {
  if (error instanceof AppApiError) {
    return error;
  }

  if (isAxiosError<ApiErrorPayload>(error)) {
    const status = error.response?.status;
    const data = error.response?.data;

    const isTimeout = error.code === 'ECONNABORTED';
    const isNetworkError = !error.response;

    let userMessage = data?.userMessage || defaultMessage || getFallbackMessageByStatus(status);

    if (isTimeout) {
      userMessage = translateFile('utils.error', 'timeout');
    } else if (isNetworkError) {
      userMessage = translateFile('utils.error', 'offline');
    }

    return new AppApiError({
      message: data?.message || error.message,
      userMessage,
      status,
      code: data?.code,
      errors: data?.errors,
      fieldErrors: Array.isArray(data?.fieldErrors) ? data.fieldErrors : undefined,
      raw: error,
    });
  }

  return new AppApiError({
    message: error instanceof Error ? error.message : 'Unknown error',
    userMessage: defaultMessage || translateFile('utils.error', 'generic'),
    raw: error,
  });
};

export const getUserMessage = (error: unknown, defaultMessage?: string) => {
  return normalizeApiError(error, defaultMessage).userMessage;
};
